import { Dispatch } from 'redux';
import { Services } from '../../types/services';
import { MsalAuthenticationResult } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';

const GET_USERS = 'GET_USERS';
const USERS_ERROR = 'USERS_ERROR';
const GET_SESSION = 'GET_SESSION';
const SESSION_READY = 'SESSION_READY';

interface User {
  address: null;
  assignedPracticeCount: number;
  city: null;
  country: null;
  createdByFirstName: null;
  createdById: null;
  createdByLastName: null;
  createdDate: null;
  displayId: number;
  email: null;
  firstName: string;
  id: string;
  idpObjectId: null;
  lastName: string;
  modifiedById: null;
  modifiedDate: null;
  partner: null;
  partnerId: null;
  partners: unknown[];
  phone: null;
  postalCode: null;
  region: null;
  role: null;
  roleId: number;
  stateProvince: null;
  timeZone: null;
  website: null;
}

interface Action {
  type: typeof GET_USERS | typeof GET_SESSION | typeof SESSION_READY | typeof USERS_ERROR;
  payload: User[] | string | boolean | number;
}

interface State {
  users: User[];
  session: string;
  sessionReady: boolean;
  clickedUser: string;
  loading: boolean;
  error: string;
}

const initialState: State = {
  users: [],
  session: '',
  sessionReady: false,
  clickedUser: '',
  loading: true,
  error: '',
};

export const reducer = (state = initialState, action: Action) => {
  switch (action.type) {
    case GET_USERS:
      return {
        ...state,
        users: action.payload,
        loading: false,
        error: '',
      };
    case GET_SESSION:
      return {
        ...state,
        session: action.payload,
      };
    case SESSION_READY:
      return {
        ...state,
        sessionReady: action.payload,
      };
    case USERS_ERROR:
      return {
        ...state,
        users: [],
        loading: false,
        error: action.payload,
      };
    default:
      return state;
  }
};

export const actions = (services: Services) => {
  const axiosService = services.axios.Axios();
  const sessionService = services.session;
  const authService = services.auth;
  const cookiesService = services.cookies;

  const getAssignableUsers = () => async () => axiosService.get('accounts/assignable');

  const fetchAssignableUsers = () => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosService.get<User[]>(`accounts/assignable`);
        const users = response.data;
        dispatch({ type: GET_USERS, payload: users });
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch({ type: USERS_ERROR, payload: errorMsg });
      }
    };
  };

  const sessionLogin = (
    loginFunction: MsalAuthenticationResult['login'],
    interactionType: InteractionType,
    requestObject: { scopes: string[] },
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        sessionService.delete(`sessionKey`);
        sessionService.delete(`token`);
        cookiesService.clearCookies();
        dispatch({ type: GET_SESSION, payload: '' });
        dispatch({ type: SESSION_READY, payload: false });
        await loginFunction(interactionType, requestObject);
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch({ type: USERS_ERROR, payload: errorMsg });
      }
    };
  };

  const sessionLogout = () => {
    return async (dispatch: Dispatch) => {
      try {
        sessionService.delete(`token`);
        cookiesService.clearCookies();
        dispatch({ type: GET_SESSION, payload: '' });
        dispatch({ type: SESSION_READY, payload: false });
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch({ type: USERS_ERROR, payload: errorMsg });
      } finally {
        authService.SignOut();
      }
    };
  };

  return {
    fetchAssignableUsers,
    getAssignableUsers,
    sessionLogin,
    sessionLogout,
  };
};
